import { useMutation } from '@apollo/client';
import { track } from '@bnnvara/analytics/src/track';
import { BaseForm } from '@bnnvara/forms/src/new_components/BaseForm/BaseForm';
import { Checkbox } from '@bnnvara/forms/src/new_components/inputs/Checkbox/Checkbox';
import { Step, StepPanel, Steps } from '@bnnvara/stepper';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useState } from 'react';

import { Membership } from '../../api/mutations/membership.gql';
import { utmParams } from '../../helpers/utmParams';
import { ContactDataStep } from './ContactDataStep';
import { PaymentStep } from './PaymentStep';
import { PersonalDataStep } from './PersonalDataStep';
import * as S from './styled';

const Form = ({
  membershipMutation,
  actionCode,
  membershipCost,
  check,
  formLabel,
  premiumCode,
  formTitle,
}) => {
  const stepper = 'membership-form';
  const [step, setStep] = useState(0);
  const [paymentData, setPaymentData] = useState({
    isLoading: true,
    paymentUrl: undefined,
    message: undefined,
    data: {},
  });
  const onSubmitPersonalData = useCallback(() => {
    setStep(1);
  }, []);

  const onSubmitContactData = useCallback(() => {
    setStep(2);
  }, []);

  async function submitHandler(params) {
    track(' lid-worden', 'click', 'doorgaan-naar-betalen');

    const houseNumber = parseInt(params.houseNumber, 10);
    const { membership, ...rest } = params;
    const values = {
      MembershipInput: {
        ...rest,
        membershipCost,
        check,
        actionCode,
        premiumCode,
        houseNumber,
        utm: utmParams(),
        redirectUrl: `${window.document.location.origin}${window.document.location.pathname}`,
      },
    };

    const result = await membershipMutation({
      variables: values,
    });

    if (result.data.membership.error) {
      setPaymentData({
        paymentUrl: undefined,
        isLoading: false,
        message: 'Er is iets fout gegaan, probeer het later nog eens',
        data: {},
      });
    } else {
      setPaymentData({
        paymentUrl: result.data.membership.url,
        isLoading: false,
        message: undefined,
        data: {
          ...rest,
          houseNumber,
          membership: formLabel,
        },
      });
    }

    return false;
  }

  return (
    <Fragment>
      <S.Wrapper>
        <S.Heading element="h2" variant="one">
          {formTitle}
        </S.Heading>

        <BaseForm id="membership-form" onSubmit={submitHandler}>
          <div style={{ display: step < 2 ? 'block' : 'none' }}>
            <Checkbox
              name="membership"
              value
              disabled={step === 2}
              label={formLabel}
              validation="accepted"
            />
          </div>

          <Steps stepper={stepper} onChange={setStep} current={step}>
            <Step label="Persoonlijke gegevens" />
            <Step label="Contactgegevens" disabled={step < 1} />
            <Step label="Betaling" disabled={step < 2} />
          </Steps>

          <StepPanel stepper={stepper} index={0} current={step}>
            <PersonalDataStep onSubmit={onSubmitPersonalData} />
          </StepPanel>

          <StepPanel stepper={stepper} index={1} current={step}>
            <ContactDataStep onSubmit={onSubmitContactData} />
          </StepPanel>

          <StepPanel stepper={stepper} index={2} current={step}>
            <PaymentStep
              paymentUrl={paymentData.paymentUrl}
              isLoading={paymentData.isLoading}
              message={paymentData.message}
              data={paymentData.data}
            />
          </StepPanel>

          <S.Privacy>
            BNNVARA houdt rekening met uw privacy conform de huidige wetgeving.
            Lees hier meer over in ons{' '}
            <S.Link
              target="_blank"
              rel="noopener noreferrer"
              href="https://bnnvara.nl/pagina/privacy-en-cookie-statement"
            >
              Privacy en Cookie statement
            </S.Link>
            .
          </S.Privacy>
        </BaseForm>
      </S.Wrapper>
    </Fragment>
  );
};

Form.propTypes = {
  membershipMutation: PropTypes.func.isRequired,
  actionCode: PropTypes.string.isRequired,
  membershipCost: PropTypes.number.isRequired,
  check: PropTypes.string.isRequired,
  formLabel: PropTypes.string.isRequired,
  premiumCode: PropTypes.string,
  formTitle: PropTypes.string.isRequired,
};

export const MembershipForm = (props) => {
  const [membership] = useMutation(Membership);

  return <Form membershipMutation={membership} {...props} />;
};
